import { useEffect, useState } from 'react';

import { useMediaPlayerContext } from '@/components/app/contentPlayer/MediaPlayerProvider';
import GuideInfo from '@/components/app/guideV2/GuideInfo';
import ShareHandoff from '@/components/app/shareFreeContent/ShareHandoff';
import SharePayment from '@/components/app/shareFreeContent/SharePayment';
import SharePrimer from '@/components/app/shareFreeContent/SharePrimer';
import { useShareContext } from '@/components/app/shareFreeContent/ShareProvider';
import Loader from '@/components/Loader';
import StripeElementsProvider from '@/components/purchase/StripeElementsProvider';
import UpsellBanner from '@/components/UpsellBanner';
import { useSimplifiedSignupModalContext } from '@/context/SimplifiedSignupModalContextProvider';
import { useAnalytics } from '@/hooks/analytics';
import { useUserState } from '@/hooks/store';
import { useIsRouting } from '@/hooks/utils/useIsRouting';
import { userCanTrial } from '@/utils/subscriptions';

import { GuideLayoutProps } from './types';

const GuideLayout = ({ guide, program }: GuideLayoutProps) => {
	const user = useUserState();
	const isRouting = useIsRouting();
	const { logEvent } = useAnalytics();
	const { onStop, playerMode } = useMediaPlayerContext();
	const { setIsModalOpen } = useSimplifiedSignupModalContext();
	const { shareToken, shareSenderFirstName, isFetchingShare } = useShareContext();

	const [startedContent, setStartedContent] = useState(false);

	const isPreviewMode = !guide?.is_free && !user?.subscription?.valid;

	useEffect(() => {
		logEvent({ eventName: 'Player Program : Landed', eventProps: { design: 'redesign-phase2' } });
	}, [logEvent]);

	useEffect(() => {
		logEvent({
			eventName: 'Screen : Viewed',
			eventProps: {
				screen: 'Signup Primer Screen',
				source: 'Shared Link',
				user_eligible_for_30D_trial: userCanTrial(user),
				share_token: shareToken as string,
				sender_first_name: shareSenderFirstName ? (shareSenderFirstName as string) : null,
				author_name: program?.author?.name || null,
				program_title: program?.title || null,
				program_id: program?.id || null,
				meditation_type: program?.meditation_type || null,
				narrator_name: program?.narrator?.name || null,
				program_language: program?.language || null,
				program_is_free: guide?.is_free || false,
				narrator_id: program?.narrator?.id || null,
				design: 'redesign-phase2',
			},
		});
	}, [shareSenderFirstName, shareToken, program, guide, logEvent, user]);

	useEffect(() => {
		if (playerMode === 'stopped' && isPreviewMode && startedContent) {
			setIsModalOpen(true);
		}
		if (playerMode === 'playing' && !startedContent) {
			setStartedContent(true);
		}
	}, [onStop, isPreviewMode, playerMode, setIsModalOpen, startedContent]);

	return (
		<StripeElementsProvider isStripeNext>
			<UpsellBanner />
			<SharePrimer />
			<SharePayment />
			<ShareHandoff />
			{isRouting || isFetchingShare ? <Loader /> : <GuideInfo program={program} guide={guide} />}
		</StripeElementsProvider>
	);
};

export default GuideLayout;
